import { useEffect, useState } from "react";
import { Menu, X, Wrench, Phone } from "lucide-react";

const links = [
  { href: "#accueil", label: "Accueil" },
  { href: "#processus", label: "Processus" },
  { href: "#services", label: "Services" },
  { href: "#reprogrammation", label: "Reprogrammation" },
  { href: "#vehicules", label: "Véhicules" },
  { href: "#avis", label: "Avis" },
  { href: "#contact", label: "Contact" },
];

export default function Header({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/70 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="#accueil"
          data-testid="logo-link"
          className="flex items-center gap-2 group"
        >
          <span className="h-9 w-9 grid place-items-center rounded-md bg-slate-900 text-white group-hover:bg-blue-600 transition-colors">
            <Wrench size={18} strokeWidth={2.4} />
          </span>
          <span
            className={`font-display font-black text-lg tracking-tight ${
              scrolled ? "text-slate-900" : "text-white drop-shadow"
            }`}
          >
            Deschamps <span className="text-blue-500">Auto</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-blue-600 hover:after:w-full after:transition-all after:duration-300 ${
                scrolled ? "text-slate-700 hover:text-blue-600" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+33649580664"
            data-testid="header-phone"
            className={`hidden lg:inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap transition-colors ${
              scrolled ? "text-slate-700 hover:text-blue-600" : "text-white/90 hover:text-white"
            }`}
          >
            <Phone size={14} /> 06 49 58 06 64
          </a>
          <button
            data-testid="header-cta-quote"
            onClick={onQuoteClick}
            className="lift inline-flex items-center rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 shadow-sm whitespace-nowrap"
          >
            Demander un devis
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden h-10 w-10 grid place-items-center rounded-md ${
            scrolled ? "text-slate-900" : "text-white"
          }`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="py-2.5 px-2 rounded-md text-slate-700 hover:bg-slate-100 font-medium"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-cta-quote"
              onClick={() => {
                setOpen(false);
                onQuoteClick?.();
              }}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white"
            >
              Demander un devis
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
